import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  areaUnitLabel,
  hectaresToUnit,
  useArea,
  useAreaUnit,
} from "common/utils";
import { useFarmerImage } from "model/useInsightsQueries";
import React, { useCallback, useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import {
  useBiodiversityScalars,
  useBirdPieData,
  useBirdScalar,
  useHabitatByName,
} from "../hooks/useInsightsDashboardQueries";

const ASSETS_BASE = "https://ruumi-dashboard-assets.ruumi.io";

const COLOR_PRODUCTION = "#A48137";
const COLOR_NATURE = "#2E7831";

type HabitatInfo = {
  description: string;
  photo?: string;
};

const unsplash = (id: string) =>
  `https://images.unsplash.com/photo-${id}?w=800&h=400&fit=crop&auto=format`;

const HABITAT_CONTEXT: Record<string, HabitatInfo> = {
  hedgerows: {
    description:
      "Hedgerows are living boundaries that provide corridors for wildlife movement, nesting sites for birds, and shelter for livestock. They also help reduce soil erosion and improve water quality by filtering runoff.",
    photo: unsplash("1761462620287-88d58c5a61f3"),
  },
  woodland: {
    description:
      "Native woodland supports a wide range of species from fungi and invertebrates to birds and mammals. Trees sequester carbon, stabilise soil, and provide shelter that can improve livestock welfare and reduce heat stress.",
    photo: `${ASSETS_BASE}/woodland_photo.jpg`,
  },
  "woodland and forest": {
    description:
      "Native woodland supports a wide range of species from fungi and invertebrates to birds and mammals. Trees sequester carbon, stabilise soil, and provide shelter that can improve livestock welfare and reduce heat stress.",
    photo: `${ASSETS_BASE}/woodland_photo.jpg`,
  },
  "woodland pasture": {
    description:
      "A traditional land use where mature trees are scattered across grazed pasture. Woodland pasture supports rare lichens, invertebrates and hole-nesting birds while providing shade and shelter for livestock.",
    photo: `${ASSETS_BASE}/woodland_pasture.png`,
  },
  "modified grassland": {
    description:
      "Grassland that has been improved through reseeding, fertilisation or drainage. While less species-rich than semi-natural grassland, well-managed modified grassland can still support pollinators and ground-nesting birds.",
    photo: unsplash("1620123357390-86c161acbb7e"),
  },
  "temporary grass and clover leys": {
    description:
      "Short-term grass and clover mixtures that fix atmospheric nitrogen, reducing the need for synthetic fertiliser. Clover-rich swards support pollinators and improve soil structure between arable rotations.",
    photo: `${ASSETS_BASE}/temp_grass_and_clover_photo.jpg`,
  },
  arable: {
    description:
      "Cropland used for growing cereals, root crops or other annual crops. Biodiversity value depends on management — field margins, cover crops and reduced tillage can significantly boost invertebrate and bird populations.",
    photo: unsplash("1596047782001-606d3e0cf41d"),
  },
  cropland: {
    description:
      "Cropland used for growing cereals, root crops or other annual crops. Biodiversity value depends on management — field margins, cover crops and reduced tillage can significantly boost invertebrate and bird populations.",
    photo: unsplash("1596047782001-606d3e0cf41d"),
  },
  "canals and ditches": {
    description:
      "Freshwater habitats that support aquatic plants, amphibians, dragonflies and water voles. Well-maintained ditches also improve field drainage and help manage flood risk across the farm.",
    photo: unsplash("1624777043760-7208103bc3f2"),
  },
  "purple moor grass": {
    description:
      "A wetland grass habitat found on poorly drained acidic soils. Purple moor grass supports specialised invertebrates and ground-nesting birds, and is an indicator of low-intensity, traditional management.",
    photo: `${ASSETS_BASE}/purple_moor.png`,
  },
  scrub: {
    description:
      "Dense woody vegetation that provides excellent cover for nesting birds, small mammals and reptiles. Scrub edges are particularly valuable as transition zones between open habitats and woodland.",
    photo: unsplash("1622789095150-102096146766"),
  },
  "other habitats": {
    description:
      "A mix of additional habitat types found on your farm. Each contributes to the overall mosaic of landscapes that supports diverse wildlife communities.",
  },
  grassland: {
    description:
      "Semi-natural grassland that has not been significantly improved. These species-rich meadows support wildflowers, pollinators, and ground-nesting birds — often the most biodiverse habitats on a farm.",
    photo: unsplash("1642021495046-32fbfebf2870"),
  },
  "floodplain wetland mosaic and cfgm": {
    description:
      "A complex of wet grassland, marsh and seasonally flooded areas. These habitats support wading birds, amphibians and rare wetland plants, and play a crucial role in natural flood management.",
    photo: unsplash("1561385882-93e9084e1a7f"),
  },
  "standing open water and canals": {
    description:
      "Ponds, lakes and canal sections that provide essential freshwater habitat for amphibians, dragonflies, aquatic plants and visiting waterfowl. Even small ponds can be biodiversity hotspots.",
    photo: unsplash("1565678282507-b06d6ffa77a2"),
  },
  "built-up areas and gardens": {
    description:
      "Gardens, farmyards and built areas can support pollinators, hedgehogs and nesting birds when managed with biodiversity in mind — through nest boxes, wildflower patches and reduced pesticide use.",
    photo: unsplash("1713656275767-e8140c59acca"),
  },
  buildings: {
    description:
      "Farm buildings can provide roosting sites for bats, nesting opportunities for barn owls and swallows, and overwintering habitat for invertebrates.",
    photo: unsplash("1767447613696-66690a9dbd14"),
  },
  "suburban mosaic of developed and natural surface": {
    description:
      "A patchwork of built areas and natural features. Green corridors, hedges and trees within these areas help connect habitats and support urban wildlife.",
    photo: unsplash("1694556586916-7b5912ba8e62"),
  },
};

const DEFAULT_HABITAT: HabitatInfo = {
  description:
    "A habitat type recorded on your farm that contributes to local biodiversity. A diverse mix of habitats supports a wider range of species.",
};

function getHabitatContext(name: string): HabitatInfo {
  const key = name.toLowerCase().trim();
  return HABITAT_CONTEXT[key] ?? DEFAULT_HABITAT;
}

function formatNumber(
  value: number | string | undefined | null,
  maxDecimals = 1
): string {
  if (value == null) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

export type Habitat = {
  name: string;
  group: string;
  areaHa: number;
  lengthM: number;
};

export function BiodiversityTab({ farmerGuid }: { farmerGuid?: string }) {
  const { fromHa, label: areaLabel } = useArea();
  const { data: bioData, isLoading: bioLoading } =
    useBiodiversityScalars(farmerGuid);
  const { data: birdData, isLoading: birdLoading } = useBirdScalar(farmerGuid);
  const { data: birdPieData, isLoading: birdPieLoading } =
    useBirdPieData(farmerGuid);
  const { data: habitatByName, isLoading: habitatByNameLoading } =
    useHabitatByName(farmerGuid);
  const { data: farmerImage } = useFarmerImage(farmerGuid);

  const bioRow = bioData?.data?.[0];
  const birdCount = birdData?.data?.[0]?.["BirdObservations.distinctBirdCount"];

  const habitats: Habitat[] = (habitatByName?.data ?? [])
    .filter((r) => r["Biodiversity.habitatName"])
    .map((r) => ({
      name: r["Biodiversity.habitatName"],
      group: r["Biodiversity.habitatGroup"],
      areaHa: Number(r["Biodiversity.totalAreaHa"]) || 0,
      lengthM: Number(r["Biodiversity.totalLengthM"]) || 0,
    }));

  const birdSpecies: string[] = (birdPieData?.data ?? [])
    .map((r) => r["BirdObservations.bird"])
    .filter(Boolean);

  const isLoading = bioLoading || birdLoading;
  const hasData =
    habitats.length > 0 ||
    birdSpecies.length > 0 ||
    (birdCount != null && Number(birdCount) > 0) ||
    (bioRow?.["Biodiversity.distinctHabitatCodeCount"] != null &&
      Number(bioRow["Biodiversity.distinctHabitatCodeCount"]) > 0);

  if (!isLoading && !hasData) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Card>
          <Typography variant="body2" sx={{ color: "#71717a" }}>
            No biodiversity data available yet. Habitat and species information
            will appear here once your farm assessments have been processed.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      {/* Overview */}
      <Card>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b", mb: 1 }}
        >
          Biodiversity Overview
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#52525b", lineHeight: 1.7, mb: 2 }}
        >
          Your farm supports a variety of habitats that provide homes for
          wildlife, improve soil health, and help manage water. Tap on a habitat
          or bird species below to learn more about its ecological value.
        </Typography>

        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 3 },
            }}
          >
            <MetricItem>
              <MetricLabel>Habitat Types</MetricLabel>
              <MetricValue>
                {formatNumber(
                  bioRow?.["Biodiversity.distinctHabitatCodeCount"]
                )}
                <MetricUnit>recorded</MetricUnit>
              </MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricLabel>Bird Species</MetricLabel>
              <MetricValue>
                {formatNumber(birdCount)}
                <MetricUnit>observed</MetricUnit>
              </MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricLabel>Hedgerows</MetricLabel>
              <MetricValue>
                {formatNumber(bioRow?.["Biodiversity.totalLengthM"], 0)}
                <MetricUnit>m</MetricUnit>
              </MetricValue>
            </MetricItem>
            <MetricItem>
              <MetricLabel>Woodland</MetricLabel>
              <MetricValue>
                {formatNumber(
                  fromHa(Number(bioRow?.["Biodiversity.totalAreaHa"]) || 0)
                )}
                <MetricUnit>{areaLabel}</MetricUnit>
              </MetricValue>
            </MetricItem>
          </Box>
        )}
      </Card>

      {/* Interactive Habitat Chart */}
      {habitatByNameLoading ? (
        <Card>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </Card>
      ) : (
        <HabitatChart habitats={habitats} />
      )}

      {/* Bird Observations */}
      {birdPieLoading ? (
        <Card>
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </Card>
      ) : (
        birdSpecies.length > 0 && (
          <BirdObservationsSection species={birdSpecies} />
        )
      )}

      {/* Map image — at the bottom */}
      {farmerImage?.url && (
        <Card>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
          >
            Your Farm Biodiversity Map
          </Typography>
          <Box
            component="img"
            src={farmerImage.url}
            alt="Biodiversity Map"
            sx={{ width: "100%", borderRadius: 2 }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              (e.currentTarget.parentElement as HTMLElement).style.display =
                "none";
            }}
          />
        </Card>
      )}
    </Box>
  );
}

// Linear habitats measured in meters, not area — exclude from bar chart
const LINEAR_HABITATS = new Set(["hedgerows", "canals and ditches"]);

// Production habitats — farmland used primarily for agricultural output
const PRODUCTION_HABITATS = new Set([
  "modified grassland",
  "temporary grass and clover leys",
  "arable",
  "cropland",
  "arable and horticulture",
  "built-up areas and gardens",
  "buildings",
  "suburban mosaic of developed and natural surface",
]);

function isProductionHabitat(name: string): boolean {
  return PRODUCTION_HABITATS.has(name.toLowerCase().trim());
}

function isLinearHabitat(name: string): boolean {
  return LINEAR_HABITATS.has(name.toLowerCase().trim());
}

type BarEntry = {
  name: string;
  value: number;
  unit: string;
  divider?: boolean;
};

// ─── Habitat bar list (used as a column within the single card) ───
function HabitatBarList({
  data,
  color,
  selectedName,
  onSelect,
}: {
  data: BarEntry[];
  color: string;
  selectedName: string | null;
  onSelect: (name: string) => void;
}) {
  const maxValue = Math.max(...data.map((d) => d.value), 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {data.map((d) => {
        const pct = (d.value / maxValue) * 100;
        const isSelected = selectedName === d.name;

        return (
          <React.Fragment key={d.name}>
            {d.divider && (
              <Box sx={{ borderTop: "1px solid #e4e4e7", mt: 1, mb: 0.5 }} />
            )}
            <Box
              onClick={() => onSelect(d.name)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                cursor: "pointer",
                p: 1,
                mx: -1,
                borderRadius: 1.5,
                backgroundColor: isSelected ? "#F7FCF1" : "transparent",
                "&:hover": {
                  backgroundColor: isSelected ? "#F7FCF1" : "#f9fafb",
                },
                transition: "background-color 0.15s",
                opacity: selectedName && !isSelected ? 0.55 : 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: 130,
                  flexShrink: 0,
                  fontSize: "0.8rem",
                  color: "#3f3f46",
                  fontWeight: isSelected ? 600 : 400,
                  textTransform: "capitalize",
                  lineHeight: 1.3,
                }}
              >
                {d.name}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 18,
                  backgroundColor: "#f4f4f5",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    minWidth: pct > 0 ? 4 : 0,
                    height: "100%",
                    backgroundColor: color,
                    borderRadius: 1,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  width: 60,
                  flexShrink: 0,
                  textAlign: "right",
                  color: "#71717a",
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  fontSize: "0.75rem",
                }}
              >
                {d.unit === "m"
                  ? `${formatNumber(d.value, 0)} m`
                  : `${formatNumber(d.value)} ${d.unit}`}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ─── Bird species context data ───
type BirdInfo = {
  description: string;
  habitat: string;
  status?: string;
};

const BIRD_CONTEXT: Record<string, BirdInfo> = {
  skylark: {
    description:
      "A ground-nesting farmland bird known for its hovering song flight. Skylarks need open fields with short vegetation for nesting and are a key indicator of healthy farmland ecosystems.",
    habitat: "Open grassland, arable fields",
    status: "Red list",
  },
  lapwing: {
    description:
      "A distinctive wading bird with an iridescent green back and wispy crest. Lapwings nest on bare or sparsely vegetated ground and have declined severely due to changes in farming practice.",
    habitat: "Wet grassland, arable fields",
    status: "Red list",
  },
  curlew: {
    description:
      "Europe's largest wading bird, recognised by its long down-curved bill and haunting call. Curlews breed on upland and lowland grassland and are one of the UK's most pressing conservation priorities.",
    habitat: "Wet grassland, moorland",
    status: "Red list",
  },
  "barn owl": {
    description:
      "A silent nocturnal hunter that relies on rough grassland margins and field edges for hunting small mammals. Barn owls benefit from nest boxes on farms and are sensitive to rodenticide use.",
    habitat: "Farmland margins, rough grassland",
  },
  swallow: {
    description:
      "A long-distance migrant that nests in farm buildings. Swallows feed on flying insects and their presence indicates a healthy invertebrate population on the farm.",
    habitat: "Farm buildings, open fields",
  },
  "house martin": {
    description:
      "A summer visitor that builds mud nests under the eaves of farm buildings. House martins feed on aerial insects and benefit from nearby water sources for nest construction.",
    habitat: "Farm buildings, open fields",
  },
  yellowhammer: {
    description:
      "A brightly coloured bunting that relies on hedgerows for nesting and stubble fields for winter feeding. Yellowhammers have declined significantly with the loss of winter stubbles.",
    habitat: "Hedgerows, arable margins",
    status: "Red list",
  },
  linnet: {
    description:
      "A small finch that feeds on weed seeds in arable fields and nests in thick hedgerows and scrub. Linnets benefit from wild bird seed mixes and uncropped field margins.",
    habitat: "Hedgerows, scrub, arable margins",
    status: "Red list",
  },
  "grey partridge": {
    description:
      "A native gamebird that has suffered dramatic declines. Grey partridges need beetle-rich field margins for chick survival and overwinter stubble for food and cover.",
    habitat: "Arable fields, grassland margins",
    status: "Red list",
  },
  kestrel: {
    description:
      "A hovering falcon that hunts small mammals in rough grassland. Kestrels benefit from field margins, conservation headlands and nest boxes on farm buildings.",
    habitat: "Open farmland, rough grassland",
    status: "Amber list",
  },
  buzzard: {
    description:
      "A large, broad-winged raptor commonly seen soaring over farmland. Buzzards feed on rabbits, voles and carrion and are a sign of a healthy rural food chain.",
    habitat: "Farmland with woodland edges",
  },
  "red kite": {
    description:
      "A graceful raptor with a distinctive forked tail, once almost extinct in the UK. Red kites primarily scavenge and their recovery is a major conservation success story.",
    habitat: "Mixed farmland, woodland edges",
  },
  robin: {
    description:
      "A familiar garden and farmland bird that follows soil disturbance to find invertebrates. Robins nest in hedgerows, banks and farm buildings.",
    habitat: "Hedgerows, gardens, woodland edges",
  },
  wren: {
    description:
      "One of the most abundant UK birds, the wren nests in hedgerows and dense scrub. Despite its tiny size, the wren has a remarkably loud song relative to its body weight.",
    habitat: "Hedgerows, scrub, woodland",
  },
  "song thrush": {
    description:
      "A melodious bird that feeds on snails and earthworms. Song thrushes need damp, invertebrate-rich grassland and thick hedgerows for nesting.",
    habitat: "Hedgerows, grassland, gardens",
    status: "Red list",
  },
  blackbird: {
    description:
      "A common thrush found across farmland. Blackbirds feed on earthworms and berries and nest in hedgerows, making them a good indicator of hedgerow health.",
    habitat: "Hedgerows, gardens, woodland edges",
  },
  "blue tit": {
    description:
      "A colourful small bird that readily uses nest boxes. Blue tits feed their chicks caterpillars from hedgerow trees and are important controllers of pest insects.",
    habitat: "Hedgerows, woodland, gardens",
  },
  "great tit": {
    description:
      "The largest European tit, nesting in tree holes and nest boxes. Great tits are adaptable feeders and important for controlling moth caterpillars on farmland trees.",
    habitat: "Hedgerows, woodland, gardens",
  },
  "house sparrow": {
    description:
      "Once abundant on farms, house sparrows have declined significantly. They nest in farm buildings and feed on grain and weed seeds in yards and field margins.",
    habitat: "Farm buildings, hedgerows",
    status: "Red list",
  },
  starling: {
    description:
      "A gregarious bird that probes grassland for leatherjackets and other invertebrates. Starlings nest in farm buildings and old trees and form spectacular winter murmurations.",
    habitat: "Grassland, farm buildings",
    status: "Red list",
  },
  raven: {
    description:
      "The largest passerine bird, often seen on upland farms. Ravens are intelligent scavengers that nest on crags and in tall trees, and their presence indicates a diverse farmland landscape.",
    habitat: "Upland farmland, moorland",
  },
  "pied wagtail": {
    description:
      "An active, tail-wagging bird commonly seen around farm buildings and livestock yards. Pied wagtails feed on insects disturbed by grazing animals.",
    habitat: "Farm buildings, grassland, water edges",
  },
  pheasant: {
    description:
      "Originally introduced for shooting, pheasants are now widespread on farmland. They nest in hedgerow bases and field margins and feed on seeds and invertebrates.",
    habitat: "Hedgerows, woodland edges, arable fields",
  },
  snipe: {
    description:
      "A well-camouflaged wading bird that breeds on wet grassland and boggy ground. Snipe are an indicator of healthy wetland habitats and benefit from raised water tables.",
    habitat: "Wet grassland, boggy ground",
    status: "Amber list",
  },
  oystercatcher: {
    description:
      "A striking black-and-white wader with a bright orange bill. Oystercatchers increasingly nest on farmland and need undisturbed ground during the breeding season.",
    habitat: "Grassland near water, arable fields",
    status: "Amber list",
  },
  heron: {
    description:
      "A large, patient waterbird that hunts fish, frogs and small mammals along farm waterways. Herons indicate healthy freshwater habitats and benefit from well-maintained ditches and ponds.",
    habitat: "Waterways, ponds, wet grassland",
  },
  "grey heron": {
    description:
      "A large, patient waterbird that hunts fish, frogs and small mammals along farm waterways. Grey herons indicate healthy freshwater habitats and benefit from well-maintained ditches and ponds.",
    habitat: "Waterways, ponds, wet grassland",
  },
};

const DEFAULT_BIRD: BirdInfo = {
  description:
    "A bird species observed on your farm, contributing to the overall avian biodiversity of your land.",
  habitat: "Various farmland habitats",
};

export function getBirdContext(name: string): BirdInfo {
  const key = name.toLowerCase().trim();
  return BIRD_CONTEXT[key] ?? DEFAULT_BIRD;
}

const STATUS_COLORS: Record<string, string> = {
  "Red list": "#D32F2F",
  "Amber list": "#A48137",
};

// ─── Bird observations section ───
export function BirdObservationsSection({
  species,
  subtitle,
}: {
  species: string[];
  subtitle?: string;
}) {
  const [selectedName, setSelectedName] = useState<string | null>(
    species[0] ?? null
  );

  const onSelect = useCallback((name: string) => {
    setSelectedName((prev) => (prev === name ? null : name));
  }, []);

  const selectedContext = selectedName ? getBirdContext(selectedName) : null;

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.25 }}
      >
        Bird Observations
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", display: "block", mb: 2 }}
      >
        {subtitle ?? `${species.length} species recorded on your farm`}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          mb: selectedName ? 2 : 0,
        }}
      >
        {species.map((name) => {
          const isSelected = selectedName === name;
          const context = getBirdContext(name);
          const statusColor = context.status
            ? STATUS_COLORS[context.status]
            : undefined;

          return (
            <Box
              key={name}
              onClick={() => onSelect(name)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                border: "1px solid",
                borderColor: isSelected ? "#2E7831" : "#e4e4e7",
                backgroundColor: isSelected ? "#F7FCF1" : "white",
                "&:hover": {
                  borderColor: isSelected ? "#2E7831" : "#a1a1aa",
                },
                transition: "all 0.15s",
                opacity: selectedName && !isSelected ? 0.6 : 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  color: "#3f3f46",
                  fontWeight: isSelected ? 600 : 400,
                  textTransform: "capitalize",
                  lineHeight: 1.3,
                }}
              >
                {name}
              </Typography>
              {statusColor && (
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: statusColor,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Detail panel */}
      {selectedName && selectedContext && (
        <Box
          sx={{
            pt: 2,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "#18181b",
              textTransform: "capitalize",
              mb: 0.5,
            }}
          >
            {selectedName}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#71717a",
                backgroundColor: "#f4f4f5",
                px: 1,
                py: 0.25,
                borderRadius: 1,
              }}
            >
              {selectedContext.habitat}
            </Typography>
            {selectedContext.status && (
              <Typography
                variant="caption"
                sx={{
                  color: STATUS_COLORS[selectedContext.status] ?? "#71717a",
                  fontWeight: 600,
                  backgroundColor: "#FDFBEB",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                {selectedContext.status}
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "#52525b",
              lineHeight: 1.6,
              fontSize: "0.825rem",
            }}
          >
            {selectedContext.description}
          </Typography>
        </Box>
      )}
    </Card>
  );
}

// ─── Interactive chart: single card, two columns, shared detail panel ───
export function HabitatChart({ habitats }: { habitats: Habitat[] }) {
  const { fromHa, label: areaLabel } = useArea();
  const productionData: BarEntry[] = habitats
    .filter((h) => h.areaHa > 0 && isProductionHabitat(h.name))
    .map((h) => ({ name: h.name, value: fromHa(h.areaHa), unit: areaLabel }))
    .sort((a, b) => b.value - a.value);

  // Nature includes both area-based and linear (meter-based) habitats
  const natureAreaData: BarEntry[] = habitats
    .filter(
      (h) =>
        h.areaHa > 0 && !isProductionHabitat(h.name) && !isLinearHabitat(h.name)
    )
    .map((h) => ({ name: h.name, value: fromHa(h.areaHa), unit: areaLabel }))
    .sort((a, b) => b.value - a.value);

  const natureLinearData: BarEntry[] = habitats
    .filter((h) => isLinearHabitat(h.name) && h.lengthM > 0)
    .map((h, i) => ({
      name: h.name,
      value: h.lengthM,
      unit: "m",
      divider: i === 0 && natureAreaData.length > 0,
    }))
    .sort((a, b) => b.value - a.value);

  // Re-apply divider to first after sorting
  if (natureLinearData.length > 0 && natureAreaData.length > 0) {
    natureLinearData.forEach((d, i) => (d.divider = i === 0));
  }

  const natureData = [...natureAreaData, ...natureLinearData];
  const allData = [...productionData, ...natureData];

  // Default to first nature habitat so the detail panel is visible
  const defaultName = natureData[0]?.name ?? productionData[0]?.name ?? null;
  const [selectedName, setSelectedName] = useState<string | null>(defaultName);

  const onSelect = useCallback((name: string) => {
    setSelectedName((prev) => (prev === name ? null : name));
  }, []);

  const selectedEntry = selectedName
    ? allData.find((d) => d.name === selectedName)
    : null;
  const selectedContext = selectedEntry
    ? getHabitatContext(selectedEntry.name)
    : null;

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 2 }}
      >
        Habitat Summary
      </Typography>

      {/* Two-column layout on desktop, stacked on mobile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* Production column */}
        {productionData.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#71717a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "block",
                mb: 1,
              }}
            >
              Production Land
            </Typography>
            <HabitatBarList
              data={productionData}
              color={COLOR_PRODUCTION}
              selectedName={selectedName}
              onSelect={onSelect}
            />
          </Box>
        )}

        {/* Nature column */}
        {natureData.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#71717a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "block",
                mb: 1,
              }}
            >
              Nature & Conservation
            </Typography>
            <HabitatBarList
              data={natureData}
              color={COLOR_NATURE}
              selectedName={selectedName}
              onSelect={onSelect}
            />
          </Box>
        )}
      </Box>

      {/* Shared detail panel at the bottom */}
      {selectedEntry && selectedContext && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            {selectedContext.photo && (
              <Box
                component="img"
                src={selectedContext.photo}
                alt={selectedEntry.name}
                sx={{
                  width: { xs: "100%", sm: 200 },
                  height: { xs: 160, sm: 140 },
                  objectFit: "cover",
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#18181b",
                  textTransform: "capitalize",
                  mb: 0.5,
                }}
              >
                {selectedEntry.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#71717a", display: "block", mb: 1 }}
              >
                {selectedEntry.unit === "m"
                  ? `${formatNumber(selectedEntry.value, 0)} m`
                  : `${formatNumber(selectedEntry.value)} ${
                      selectedEntry.unit
                    }`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#52525b",
                  lineHeight: 1.6,
                  fontSize: "0.825rem",
                }}
              >
                {selectedContext.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}

// ─── Grouped habitat chart for programme-level view ──────────────

const MAX_VISIBLE_BARS = 8;

const GROUP_COLORS = [
  "#2E7831",
  "#A48137",
  "#7D5DA3",
  "#4A90D9",
  "#D35F5F",
  "#3AAFA9",
  "#E8833A",
  "#6B8E23",
];

type GroupEntry = {
  name: string;
  totalHa: number;
  totalM: number;
  habitats: Habitat[];
  color: string;
};

export function ProgrammeHabitatChart({ habitats }: { habitats: Habitat[] }) {
  const areaUnit = useAreaUnit();
  const areaLabel = areaUnitLabel(areaUnit);
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { totalHa: number; totalM: number; habitats: Habitat[] }
    >();
    for (const h of habitats) {
      const g = h.group || "Other";
      const entry = map.get(g) ?? { totalHa: 0, totalM: 0, habitats: [] };
      entry.totalHa += h.areaHa;
      entry.totalM += h.lengthM;
      entry.habitats.push(h);
      map.set(g, entry);
    }
    return Array.from(map.entries())
      .map(
        ([name, data], i): GroupEntry => ({
          name,
          ...data,
          color: GROUP_COLORS[i % GROUP_COLORS.length],
        })
      )
      .sort(
        (a, b) => b.totalHa + b.totalM / 1000 - (a.totalHa + a.totalM / 1000)
      );
  }, [habitats]);

  // Separate linear habitat groups (hedgerows, canals & ditches) from pie chart
  const linearGroups = groups.filter((g) =>
    g.habitats.every((h) => isLinearHabitat(h.name))
  );
  const areaGroups = groups.filter(
    (g) => !g.habitats.every((h) => isLinearHabitat(h.name))
  );

  const defaultHabitat =
    areaGroups[0]?.habitats
      .filter((h) => h.areaHa > 0 && !isLinearHabitat(h.name))
      .sort((a, b) => b.areaHa - a.areaHa)[0]?.name ?? null;

  const [selectedGroup, setSelectedGroup] = useState<string | null>(
    areaGroups[0]?.name ?? null
  );
  const [selectedHabitat, setSelectedHabitat] = useState<string | null>(
    defaultHabitat
  );

  const activeGroup = areaGroups.find((g) => g.name === selectedGroup);

  const barData: BarEntry[] = useMemo(() => {
    if (!activeGroup) return [];
    const areaEntries: BarEntry[] = activeGroup.habitats
      .filter((h) => h.areaHa > 0 && !isLinearHabitat(h.name))
      .map((h) => ({
        name: h.name,
        value: hectaresToUnit(h.areaHa, areaUnit),
        unit: areaLabel,
      }))
      .sort((a, b) => b.value - a.value);

    const linearEntries: BarEntry[] = activeGroup.habitats
      .filter((h) => isLinearHabitat(h.name) && h.lengthM > 0)
      .map((h, i) => ({
        name: h.name,
        value: h.lengthM,
        unit: "m",
        divider: i === 0 && areaEntries.length > 0,
      }))
      .sort((a, b) => b.value - a.value);

    if (linearEntries.length > 0 && areaEntries.length > 0) {
      linearEntries.forEach((d, i) => (d.divider = i === 0));
    }

    return [...areaEntries, ...linearEntries];
  }, [activeGroup, areaUnit, areaLabel]);

  const pieData = areaGroups.map((g) => ({
    name: g.name,
    value: g.totalHa,
  }));

  const onSelectGroup = useCallback((name: string) => {
    setSelectedGroup(name);
    setSelectedHabitat(null);
  }, []);

  const onSelectHabitat = useCallback((name: string) => {
    setSelectedHabitat((prev) => (prev === name ? null : name));
  }, []);

  const linearHabitats = linearGroups.flatMap((g) => g.habitats);

  const selectedEntry: BarEntry | null = selectedHabitat
    ? barData.find((d) => d.name === selectedHabitat) ??
      linearHabitats
        .filter((h) => h.name === selectedHabitat)
        .map(
          (h): BarEntry => ({ name: h.name, value: h.lengthM, unit: "m" })
        )[0] ??
      null
    : null;
  const selectedContext = selectedEntry
    ? getHabitatContext(selectedEntry.name)
    : null;

  const groupColor = activeGroup?.color ?? GROUP_COLORS[0];

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 2 }}
      >
        Habitat Summary
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* Pie chart — habitat groups */}
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: 220 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: 180, height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={75}
                  paddingAngle={2}
                  stroke="none"
                  style={{ cursor: "pointer", outline: "none" }}
                  onClick={(_, index) => onSelectGroup(areaGroups[index].name)}
                >
                  {areaGroups.map((g) => (
                    <Cell
                      key={g.name}
                      fill={g.color}
                      opacity={selectedGroup === g.name ? 1 : 0.4}
                      style={{ outline: "none" }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Group legend */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mt: 1,
              width: "100%",
            }}
          >
            {areaGroups.map((g) => (
              <Box
                key={g.name}
                onClick={() => onSelectGroup(g.name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor:
                    selectedGroup === g.name ? "#F7FCF1" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      selectedGroup === g.name ? "#F7FCF1" : "#f9fafb",
                  },
                  opacity: selectedGroup && selectedGroup !== g.name ? 0.55 : 1,
                  transition: "all 0.15s",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: g.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#3f3f46",
                    fontWeight: selectedGroup === g.name ? 600 : 400,
                    fontSize: "0.75rem",
                    lineHeight: 1.3,
                    textTransform: "capitalize",
                  }}
                >
                  {g.name}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Linear habitats (hedgerows, ditches) */}
          {linearGroups.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid #f0f0f0",
                width: "100%",
              }}
            >
              {linearHabitats.map((h) => {
                const isSelected = selectedHabitat === h.name;
                return (
                  <Box
                    key={h.name}
                    onClick={() => onSelectHabitat(h.name)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.5,
                      px: 1,
                      cursor: "pointer",
                      borderRadius: 1,
                      backgroundColor: isSelected ? "#F7FCF1" : "transparent",
                      "&:hover": {
                        backgroundColor: isSelected ? "#F7FCF1" : "#f9fafb",
                      },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#3f3f46",
                        fontSize: "0.75rem",
                        fontWeight: isSelected ? 600 : 400,
                        textTransform: "capitalize",
                      }}
                    >
                      {h.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#71717a",
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatNumber(h.lengthM, 0)} m
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Bar chart — habitats in selected group */}
        {activeGroup && barData.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#71717a",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "block",
                mb: 1,
              }}
            >
              {activeGroup.name}
            </Typography>
            <Box
              sx={{
                maxHeight: MAX_VISIBLE_BARS * 42,
                overflowY:
                  barData.length > MAX_VISIBLE_BARS ? "auto" : "visible",
                mr: barData.length > MAX_VISIBLE_BARS ? -1 : 0,
                pr: barData.length > MAX_VISIBLE_BARS ? 1 : 0,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#d4d4d8",
                  borderRadius: 2,
                },
              }}
            >
              <HabitatBarList
                data={barData}
                color={groupColor}
                selectedName={selectedHabitat}
                onSelect={onSelectHabitat}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Detail panel */}
      {selectedEntry && selectedContext && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            {selectedContext.photo && (
              <Box
                component="img"
                src={selectedContext.photo}
                alt={selectedEntry.name}
                sx={{
                  width: { xs: "100%", sm: 200 },
                  height: { xs: 160, sm: 140 },
                  objectFit: "cover",
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#18181b",
                  textTransform: "capitalize",
                  mb: 0.5,
                }}
              >
                {selectedEntry.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#71717a", display: "block", mb: 1 }}
              >
                {selectedEntry.unit === "m"
                  ? `${formatNumber(selectedEntry.value, 0)} m`
                  : `${formatNumber(selectedEntry.value)} ${
                      selectedEntry.unit
                    }`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#52525b",
                  lineHeight: 1.6,
                  fontSize: "0.825rem",
                }}
              >
                {selectedContext.description}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
});

const MetricItem = styled("div")({
  padding: "8px 0",
  borderBottom: "1px solid #f0f0f0",
  flex: 1,
  "&:last-child": {
    borderBottom: "none",
  },
  "@media (min-width: 600px)": {
    borderBottom: "none",
    borderRight: "1px solid #f0f0f0",
    padding: "0 16px",
    "&:first-of-type": {
      paddingLeft: 0,
    },
    "&:last-child": {
      borderRight: "none",
      paddingRight: 0,
    },
  },
});

const MetricLabel = styled(Typography)({
  fontSize: "0.7rem",
  color: "#71717a",
  display: "block",
});

const MetricValue = styled(Typography)({
  fontWeight: 600,
  color: "#18181b",
  fontSize: "1rem",
});

const MetricUnit = styled("span")({
  fontSize: "0.75rem",
  color: "#71717a",
  marginLeft: 4,
});
